import * as Yup from 'yup'
import { Formik } from 'formik'
import CircularProgress from '@mui/material/CircularProgress'
import { Box, Button, FormHelperText, Grid2 as Grid, TextField, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import { logError } from '../../../util/logger'
import { setIsAuthCheckLoading, setIsScratchUserLoggingIn } from '../../../slices/auth'
import {
	signInWithCustomToken,
	signInWithEmailAndPassword,
	signInWithGoogle
	// logout
} from '../../../lib/firebase'
import { generateScratchAdminCustomToken } from 'apis/auth'

const getLoginError = (message: string) => {
	if (
		message === 'Firebase: Error (auth/user-not-found).' ||
		message === 'Firebase: Error (auth/wrong-password).'
	) {
		return 'Your login credentials didn’t match our records. Please try again or reset your password if needed.'
	}
	return 'Something went wrong. Please try again later.'
}

const LoginFirebase = (props) => {
	const dispatch = useDispatch()
	return (
		<div {...props}>
			<Formik
				initialValues={{
					email: '',
					password: '',
					submit: null
				}}
				validationSchema={Yup.object().shape({
					email: Yup.string().max(255).required('Username is required'),
					//only make password required when email ends with @scratchpay.com
					password: Yup.string()
						.max(255)
						.when('email', {
							is: (email: string) => !email?.endsWith('@scratchpay.com'),
							then: Yup.string().required('Password is required')
						})
				})}
				onSubmit={async (values, { setStatus, setSubmitting, resetForm }) => {
					try {
						if (values.email.includes('@')) {
							if (values.email.endsWith('@scratchpay.com')) {
								dispatch(setIsScratchUserLoggingIn(true))
								await new Promise((resolve) => setTimeout(resolve, 1000))
								const googleSignInResult = await signInWithGoogle()
								// Sometimes scratch users use emails in the format of
								// email+somevalue@scratchpay.com
								// if this is the case, we need to sign-in with the requested email
								// not the email coming from Google because it will always be
								// email@scratchpay.com
								if (googleSignInResult.user.email !== values.email) {
									// Generate a custom token for Scratch Admin from the provided email
									const result = await generateScratchAdminCustomToken(values.email)

									const customToken = result.data?.customToken
									if (customToken) {
										// Sign out the current user
										// await logout()
										// await new Promise((resolve) => setTimeout(resolve, 4000))
										// Sign in with the custom token
										await signInWithCustomToken(customToken)
										await new Promise((resolve) => setTimeout(resolve, 1000))
									} else {
										dispatch(setIsScratchUserLoggingIn(false))
										throw new Error('Failed to generate custom token for Scratch Admin')
									}
								}
								dispatch(setIsScratchUserLoggingIn(false))
							} else {
								// If the email is already in the correct format, use it directly
								await signInWithEmailAndPassword(values.email, values.password)
							}
						} else {
							await signInWithEmailAndPassword(`${values.email}@scratchpay.care`, values.password)
						}
						dispatch(setIsAuthCheckLoading(true))
						setStatus({ success: true })
						setSubmitting(false)
					} catch (err: any) {
						dispatch(setIsAuthCheckLoading(false))
						logError(err)
						setStatus({ success: false })
						resetForm({
							values: { email: values.email, password: '', submit: null },
							errors: { submit: getLoginError(err.message) },
							touched: { email: true, password: true }
						})
						setSubmitting(false)
					}
				}}
			>
				{({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
					<form noValidate onSubmit={handleSubmit} {...props}>
						<Grid container spacing={3}>
							<Grid
								size={{
									xs: 12,
									sm: 12
								}}
							>
								<Typography sx={{ mb: -1 }} variant="subtitle2">
									Username:
								</Typography>
								<TextField
									error={Boolean(touched.email && errors.email)}
									fullWidth
									value={values.email}
									helperText={touched.email && errors.email}
									margin="normal"
									name="email"
									onBlur={handleBlur}
									onChange={handleChange}
									variant="outlined"
								/>
							</Grid>
							{!values.email.endsWith('@scratchpay.com') && (
								<Grid
									size={{
										xs: 12,
										sm: 12
									}}
								>
									<Typography sx={{ mb: -1 }} variant="subtitle2">
										Password:
									</Typography>
									<TextField
										error={Boolean(touched.password && errors.password)}
										fullWidth
										helperText={touched.password && errors.password}
										margin="normal"
										name="password"
										value={values.password}
										onBlur={handleBlur}
										onChange={handleChange}
										type="password"
										variant="outlined"
									/>
								</Grid>
							)}
						</Grid>
						{errors.submit && errors.submit !== 'The email address is badly formatted.' && (
							<Box sx={{ mt: 3 }}>
								<FormHelperText error>{errors.submit}</FormHelperText>
							</Box>
						)}
						<Box sx={{ mt: 4 }}>
							<Button
								color="primary"
								disabled={isSubmitting}
								fullWidth
								size="large"
								type="submit"
								variant="contained"
								data-cy="login-button"
							>
								Log In {values.email.endsWith('@scratchpay.com') ? 'with Google' : ''}
								{isSubmitting ? <CircularProgress sx={{ p: 1.5 }} /> : ''}
							</Button>
						</Box>
					</form>
				)}
			</Formik>
		</div>
	)
}

export default LoginFirebase
